import { Link } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut, Settings, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { signOut } from '@/features/auth/bootstrap'
import { useSessionStore } from '@/features/auth/session-store'
import { UserAvatar } from '@/shared/components/user-avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/shared/ui/sidebar'

export function UserMenu() {
  const { t } = useTranslation()
  const user = useSessionStore((state) => state.user)

  if (user === null) return null

  // Blank rather than absent: the serializer always sends a name, and an account
  // whose realm never gave it one gets the empty string.
  const label = user.first_name === '' ? user.username : user.first_name

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* The avatar leads, so the row still says whose account it is once
                the sidebar is down to icon width and the name has gone. */}
            <SidebarMenuButton size="lg" tooltip={label}>
              <UserAvatar src={user.avatar_url} seed={user.username} />
              <span className="truncate">{label}</span>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-60" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="top"
            align="start"
            className="w-(--radix-dropdown-menu-trigger-width)"
          >
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate font-medium">{label}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {user.username}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings />
                {t('settings.title')}
              </Link>
            </DropdownMenuItem>
            {/*
              Shown to staff only, which is a convenience rather than the
              control: the routes themselves refuse anybody else, because a
              hidden link is still a link that can be typed.
            */}
            {user.is_staff && (
              <DropdownMenuItem asChild>
                <Link to="/admin">
                  <Shield />
                  {t('admin.title')}
                </Link>
              </DropdownMenuItem>
            )}
            {/*
              Ends the session at the identity provider as well as here. Signing
              out only locally would leave the realm session standing, and the
              next sign-in would complete without a prompt -- which reads as the
              sign-out having failed.
            */}
            <DropdownMenuItem onSelect={signOut}>
              <LogOut />
              {t('action.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
